import React, { useState, useEffect } from 'react'
import ReactDOM from 'react-dom'
import PropTypes from 'prop-types'
import cloneDeep from 'lodash/cloneDeep'
import EmailSubscriptionCenterSignInForm from '../SignIn/EmailSubscriptionCenterSignInForm.jsx'
import SubscribedLoadingModal from './SubscribedLoadingModal.jsx'
import ModifySubscriptionModal from './ModifySubscriptionModal.jsx'
import LiteratureDocs from './LiteratureDocs.jsx'
import SubscriptionDocs from './SubscriptionDocs.jsx'
import {
  getSubscriptionsForUser,
  getStorageProfile,
  updateSubscriptions,
  modelLiteratureDocs,
} from '../Data/SubscriptionCenterDataSource.js'
import {
  ACTIVE,
  INACTIVE,
  SERVICE_LABEL_ADVISOR,
  SERVICE_LABEL_INVESTOR,
  SERVICE_LABEL_USER,
} from '../Landing/Helper/constants.js'
import {
  getAllSubscriptionsFromSubscriptionProfile,
  findSubscriptionFromSubscriptionListByPublicationID,
} from '../Landing/Helper/helper.js'

function EmailSubscriptionCenterModal(props) {
  const { handleCloseModalClick, name, isLoggedIn, handleUpdate } = props

  const [literature, setLiterature] = useState(cloneDeep(props.docs) || [])
  const [subscriptions, setSubscriptions] = useState(props.subscriptions)
  const [step, setStep] = useState(isLoggedIn ? 2 : 0)
  const [modifySuccess, setModifySuccess] = useState(false)
  const [subscribed, setsubscribed] = useState(true)
  const [modified, setModified] = useState(false)
  const [selectedRole, setSelectedRole] = useState(SERVICE_LABEL_ADVISOR)

  useEffect(() => {
    if (step === 1) {
      const fetchData = async () => {
        const postData = await getPostData()
        await updateSubscriptions(postData)
        setStep(2)
        setModifySuccess(true)
      }
      fetchData()
    }
  }, [step])

  useEffect(() => {
    if (literature && subscriptions) {
      const ids = new Set(literature.map((lit) => lit.id))

      const _subscriptions = [
        ...subscriptions.filter((sub) => !ids.has(sub.id)),
      ]

      setSubscriptions(_subscriptions)
    }
  }, [])

  const handleSubscriptionLiteratureClick = (event) => {
    const docId = event.target.value
    const docIndex = literature.findIndex((doc) => doc.id === docId)
    if (docIndex !== -1) {
      const doc = cloneDeep(literature[docIndex])
      doc.selected = event.target.checked
      setLiterature((prevLiterature) => {
        const newLiterature = [...prevLiterature]
        newLiterature[docIndex] = doc
        return newLiterature
      })
    }
  }

  const handleSubscriptionClick = (event) => {
    const _subscriptions = JSON.parse(JSON.stringify([...subscriptions]))
    const docId = event.target.value

    const doc = _subscriptions.find((doc) => doc.id === docId)
    doc.checked = event.target.checked
    doc.selected = doc.checked
    setSubscriptions(_subscriptions)
  }

  const handleSuccessLogin = async () => {
    setStep(1)
    modelLiteratureDocs?.next(literature)
  }

  const getPostData = async () => {
    const subscriptionInfo = (await getSubscriptionsForUser())?.value
    const subscriptionProfile = {
      ...getStorageProfile(),
      ...subscriptionInfo,
    }

    const _literature = literature || []
    const _subscriptions = subscriptions || []
    let dataArray = [..._literature, ..._subscriptions]

    const allSubscribed =
      getAllSubscriptionsFromSubscriptionProfile(subscriptionProfile)

    // need to check if we are already subscribed to a fund, only then do we send the unsubscribe
    dataArray = dataArray.filter((doc) => {
      if (!(doc.selected || doc.checked)) {
        const _subscribedDoc =
          findSubscriptionFromSubscriptionListByPublicationID(
            allSubscribed,
            doc.id
          )
        return _subscribedDoc && _subscribedDoc.status === 'Active'
      }
      return true
    })

    return {
      subscriptions: dataArray.map((doc) => {
        return {
          wholeSalerID: null,
          contactID: subscriptionProfile.contactID,
          publicationID: doc.id,
          status: doc.selected || doc.checked ? ACTIVE : INACTIVE,
          actor:
            subscriptionProfile.role === SERVICE_LABEL_ADVISOR
              ? SERVICE_LABEL_USER
              : SERVICE_LABEL_INVESTOR,
          updatedTimeStamp: new Date().toISOString(),
          firstName: subscriptionProfile.firstName,
          lastName: subscriptionProfile.lastName,
          userId: subscriptionProfile.emailId,
        }
      }),
    }
  }

  const handleModifyClick = async () => {
    const postData = await getPostData()
    if (
      postData &&
      postData.subscriptions &&
      postData.subscriptions.length > 0
    ) {
      updateSubscriptions(postData)
    }

    setModifySuccess(true)
    setsubscribed(false)
    setModified(true)
    modelLiteratureDocs?.next(literature)
    handleUpdate && handleUpdate()
  }

  const handleSuccessOptionChange = (selectedOption) => {
    setSelectedRole(selectedOption)
  }

  const filterDocsByRole = (role) => {
    return filterByRole(literature, role)
  }

  const filterSubscriptionsByRole = (role) => {
    return filterByRole(subscriptions, role)
  }

  const filterByRole = (list, role) => {
    return (
      list &&
      list.filter((doc) => {
        if (role === 'Advisor') {
          return doc.availableRoles.find(
            (availableRole) => availableRole === 'advisor'
          )
        } else if (role === 'Non-Advisor') {
          return doc.availableRoles.find(
            (availableRole) => availableRole === 'investor'
          )
        }
      })
    )
  }

  const getModalSideContent = () => {
    return (
      <div
        className="u-lg-size6of12 u-sm-sizeFull u-paddingTop4gu u-paddingLeft6gu u-paddingRight4gu"
        data-analytics="cesc-modal-step2"
      >
        <h3 className="u-marginBottom2gu">Step 2</h3>
        <p className="u-marginBottom4gu">
          Which emails would you like to receive?
        </p>
        <h4 className="u-marginBottom3gu">{name}</h4>
        <LiteratureDocs
          literature={filterDocsByRole(selectedRole)}
          handleSubscriptionClick={handleSubscriptionLiteratureClick}
        />
        <SubscriptionDocs
          subscriptions={filterSubscriptionsByRole(selectedRole)}
          handleSubscriptionClick={handleSubscriptionClick}
        />
      </div>
    )
  }

  const hasInvestorSubscriptions = () => {
    return (
      filterDocsByRole(SERVICE_LABEL_INVESTOR).length > 0 ||
      filterSubscriptionsByRole(SERVICE_LABEL_INVESTOR).length > 0
    )
  }

  return ReactDOM.createPortal(
    <div
      className="Modal"
      data-analytics="cesc-login-modal"
      id="email-subscription-center-modal"
    >
      <section className="Modal-window">
        <div className="Modal-content">
          <div className="u-flex u-marginBottom2gu">
            <h2 className="u-flexGrow1 u-textSizeMsrp4 u-marginTop4gu">
              Subscribe
            </h2>
            <button
              onClick={handleCloseModalClick}
              className="Modal-icon u-flexJustifyEnd u-textPrimary close-icon"
            ></button>
          </div>
          <hr
            className="Rule Rule--thin u-bgColorNeutral u-marginVert5gu u-sm-marginTop0"
            role="presentation"
          ></hr>

          {step === 0 && (
            <EmailSubscriptionCenterSignInForm
              handleSuccessLogin={handleSuccessLogin}
              modalSideContent={getModalSideContent()}
              isModal={true}
              handleSuccessOptionChange={handleSuccessOptionChange}
              hideInvestorChoice={!hasInvestorSubscriptions()}
            />
          )}

          {step === 1 && <SubscribedLoadingModal></SubscribedLoadingModal>}

          {step === 2 && (
            <ModifySubscriptionModal
              literature={filterDocsByRole(selectedRole)}
              handleSubscriptionLiteratureClick={
                handleSubscriptionLiteratureClick
              }
              subscriptions={filterSubscriptionsByRole(selectedRole)}
              handleSubscriptionClick={handleSubscriptionClick}
              handleModifyClick={handleModifyClick}
              name={name}
              modifySuccess={modifySuccess}
              subscribed={subscribed}
              modified={modified}
            />
          )}
        </div>
      </section>
    </div>,
    document.querySelector('body')
  )
}

EmailSubscriptionCenterModal.propTypes = {
  docs: PropTypes.array,
  subscriptions: PropTypes.array,
  handleCloseModalClick: PropTypes.func,
  name: PropTypes.string,
  isLoggedIn: PropTypes.bool,
}

export default EmailSubscriptionCenterModal
